import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {
    ChevronLeftRounded,
    ChevronRightRounded,
    CheckCircleRounded,
    EventAvailableRounded,
    FactoryRounded,
    LockRounded,
    LogoutRounded,
    RadioButtonUncheckedRounded,
    TodayRounded,
    UndoRounded,
} from "@mui/icons-material";
import { io } from "socket.io-client";
import AuthController from "../../Api/authController";
import factoryDeliveryNoticesController, {
    IDeliveryNotice,
    IDeliveryNoticeFactory,
    IDeliveryNotices,
} from "../../Api/factoryDeliveryNotices";
import { baseURL } from "../../config";
import { useUser } from "../../hooks/useUser";

type CalendarLog = {
    factory: IDeliveryNoticeFactory;
    notice: IDeliveryNotice;
};

const toDateKey = (date: Date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const normalizeDateKey = (value?: string | null) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return toDateKey(date);
};

const formatDate = (value: string) => {
    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) return value;

    return new Intl.DateTimeFormat("pl-PL", {
        day: "2-digit",
        month: "long",
        year: "numeric",
    }).format(date);
};

const monthLabel = (date: Date) => new Intl.DateTimeFormat("pl-PL", {
    month: "long",
    year: "numeric",
}).format(date);

const getImageUrl = (image?: string) => {
    if (!image) return "";
    if (image.startsWith("http")) return image;
    return `${baseURL}${image.replace(/\\/g, "/")}`;
};

const getMonthDays = (month: Date) => {
    const year = month.getFullYear();
    const monthIndex = month.getMonth();
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    const firstDay = new Date(year, monthIndex, 1);
    const offset = (firstDay.getDay() + 6) % 7;
    const blanks = Array.from({ length: Math.min(offset, 6) }, () => null);
    const days = Array.from({ length: daysInMonth }, (_, index) => new Date(year, monthIndex, index + 1))
        .filter((date) => date.getDay() !== 0);

    return [...blanks, ...days];
};

const getNoticeDateKeys = (notice: IDeliveryNotice) => (
    (notice.deliveryDates || []).map(normalizeDateKey).filter(Boolean).sort()
);

const getNoticeRange = (notice: IDeliveryNotice) => {
    const dates = getNoticeDateKeys(notice);
    if (dates.length === 0) return "";
    if (dates.length === 1) return formatDate(dates[0]);
    return `${formatDate(dates[0])} - ${formatDate(dates[dates.length - 1])}`;
};

const FactoryAvatar = ({ factory, size = 42 }: { factory: IDeliveryNoticeFactory; size?: number }) => {
    const imageUrl = getImageUrl(factory.image);

    return (
        <Avatar
            src={imageUrl}
            variant="rounded"
            sx={{
                width: size,
                height: size,
                borderRadius: 1.25,
                bgcolor: imageUrl ? "#fff" : "#eef2f7",
                "& img": { objectFit: "contain", p: size <= 24 ? 0.15 : 0.35 },
            }}
        >
            <FactoryRounded fontSize="small" />
        </Avatar>
    );
};

const FactoryDeliveryNoticeBoard = () => {
    const session = useUser();
    const navigate = useNavigate();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [data, setData] = useState<IDeliveryNotices | null>(null);
    const [month, setMonth] = useState(() => new Date());
    const [selectedDateKey, setSelectedDateKey] = useState(() => toDateKey(new Date()));
    const [isLoading, setIsLoading] = useState(false);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [updatingNoticeId, setUpdatingNoticeId] = useState<string | null>(null);
    const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!session.authReady) return;
        if (session.currentUser?.mustChangePassword) {
            navigate('/zmien-haslo', { state: { returnTo: '/awizacje' } });
            return;
        }
        setIsAuthenticated(session.hasPermission('deliveryNotices.read'));
    }, [session.authReady, session.currentUser]);

    const todayKey = useMemo(() => toDateKey(new Date()), []);
    const todayLabel = useMemo(() => formatDate(todayKey), [todayKey]);
    const selectedDateLabel = useMemo(() => formatDate(selectedDateKey), [selectedDateKey]);
    const isTodaySelected = selectedDateKey === todayKey;
    const monthDays = useMemo(() => getMonthDays(month), [month]);
    const visibleDateKeys = useMemo(
        () => new Set(monthDays.filter((date): date is Date => Boolean(date)).map(toDateKey)),
        [monthDays]
    );

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        const res = await factoryDeliveryNoticesController.getDeliveryNotices();

        if (!res.ok || !res.deliveryNotices) {
            setIsAuthenticated(false);
            setData(null);
            setError(res.message || "Nie pobrano awizacji");
            setIsLoading(false);
            return;
        }

        setData(res.deliveryNotices);
        setIsLoading(false);
    }, []);

    useEffect(() => {
        if (!isAuthenticated) return;
        load();
    }, [isAuthenticated, load]);

    useEffect(() => {
        if (!isAuthenticated) return;

        const socket = io(baseURL, { transports: ["websocket", "polling"] });
        socket.on("factoryDeliveryNotices:changed", () => {
            load();
        });

        return () => {
            socket.off("factoryDeliveryNotices:changed");
            socket.disconnect();
        };
    }, [isAuthenticated, load]);

    const visibleLogs = useMemo(() => {
        const logs: CalendarLog[] = [];

        (data?.factories || []).forEach((factory) => {
            (factory.notices || []).forEach((notice) => {
                if (!getNoticeDateKeys(notice).some((dateKey) => visibleDateKeys.has(dateKey))) return;
                logs.push({ factory, notice });
            });
        });

        return logs;
    }, [data?.factories, visibleDateKeys]);

    const selectedDayLogs = useMemo(() => (
        visibleLogs.filter((log) => getNoticeDateKeys(log.notice).includes(selectedDateKey))
    ), [visibleLogs, selectedDateKey]);

    const calendarLogs = useMemo(() => {
        const logs = new Map<string, CalendarLog[]>();

        visibleLogs.forEach((log) => {
            getNoticeDateKeys(log.notice).forEach((dateKey) => {
                if (!visibleDateKeys.has(dateKey)) return;
                const list = logs.get(dateKey) || [];
                list.push(log);
                logs.set(dateKey, list);
            });
        });

        return logs;
    }, [visibleLogs, visibleDateKeys]);

    const handleLogin = async (event: FormEvent) => {
        event.preventDefault();
        if (isLoggingIn || !username.trim() || !password) return;

        setIsLoggingIn(true);
        setError(null);

        const res = await AuthController.login({
            username: username.trim(),
            password,
        });

        if (!res.ok || !res.token) {
            setError(res.message || "Nie zalogowano");
            setIsLoggingIn(false);
            return;
        }

        if (res.user) session.setSession(res.user);
        setPassword("");
        const permissions = res.user?.permissions || [];
        const allowed = res.user?.role?.toLowerCase() === 'superadmin'
            || permissions.some(permission => ['*', 'deliveryNotices.*', 'deliveryNotices.read'].includes(permission));
        if (res.user?.mustChangePassword) {
            navigate('/zmien-haslo', { state: { returnTo: '/awizacje' } });
        } else if (!allowed) {
            setError('To konto nie ma uprawnienia do odczytu awizacji. Skontaktuj się z administratorem.');
        }
        setIsAuthenticated(allowed && !res.user?.mustChangePassword);
        setIsLoggingIn(false);
    };

    const handleLogout = async () => {
        await session.logout();
        setIsAuthenticated(false);
        setData(null);
        setIsLogoutDialogOpen(false);
    };

    const changeMonth = (direction: -1 | 1) => {
        setMonth((current) => new Date(current.getFullYear(), current.getMonth() + direction, 1));
    };

    const selectDate = (date: Date) => {
        setSelectedDateKey(toDateKey(date));
    };

    const returnToToday = () => {
        const today = new Date();
        setSelectedDateKey(todayKey);
        setMonth(new Date(today.getFullYear(), today.getMonth(), 1));
    };

    const setNoticeCompleted = async (factoryId: string, notice: IDeliveryNotice, completed: boolean) => {
        if (!session.hasPermission('deliveryNotices.complete') || updatingNoticeId) return;
        setUpdatingNoticeId(notice._id);
        setError(null);

        const res = await factoryDeliveryNoticesController.completeNotice(factoryId, notice._id, completed);
        if (!res.ok || !res.deliveryNotices) {
            setError(res.message || "Nie zmieniono statusu dostawy");
        } else {
            setData(res.deliveryNotices);
        }
        setUpdatingNoticeId(null);
    };

    if (!isAuthenticated) {
        return (
            <Box sx={{ minHeight: "100vh", bgcolor: "#eef2f7", display: "grid", placeItems: "center", p: 2 }}>
                <Paper elevation={0} sx={{ width: "100%", maxWidth: 380, p: 3, borderRadius: 2, border: "1px solid #dbe3ef" }}>
                    <Stack spacing={2} component="form" onSubmit={handleLogin}>
                        <Stack direction="row" gap={1.25} alignItems="center">
                            <Box sx={{ width: 46, height: 46, borderRadius: 1.5, bgcolor: "#1d4ed8", color: "#fff", display: "grid", placeItems: "center" }}>
                                <LockRounded />
                            </Box>
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 950 }}>Awizacje</Typography>
                                <Typography variant="body2" sx={{ color: "#64748b" }}>Logowanie magazynu</Typography>
                            </Box>
                        </Stack>
                        {error ? <Alert severity="error">{error}</Alert> : null}
                        <TextField
                            label="Login lub e-mail"
                            value={username}
                            onChange={(event) => setUsername(event.target.value)}
                            autoComplete="username"
                            autoFocus
                            required
                            fullWidth
                        />
                        <TextField
                            label="Hasło"
                            type="password"
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            autoComplete="current-password"
                            required
                            fullWidth
                        />
                        <Button type="submit" variant="contained" disabled={isLoggingIn || !username.trim() || !password} sx={{ fontWeight: 900 }}>
                            {isLoggingIn ? "Logowanie..." : "Zaloguj"}
                        </Button>
                    </Stack>
                </Paper>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: "100vh", bgcolor: "#eef2f7", p: { xs: 1.5, md: 2.5 } }}>
            <Stack spacing={2}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid #dbe3ef", bgcolor: "#fff" }}>
                    <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} gap={1.5}>
                        <Stack direction="row" gap={1.25} alignItems="center">
                            <Box sx={{ width: 48, height: 48, borderRadius: 1.5, bgcolor: "#1d4ed8", color: "#fff", display: "grid", placeItems: "center" }}>
                                <EventAvailableRounded />
                            </Box>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 950, letterSpacing: 0 }}>Awizacje dostaw</Typography>
                                <Typography variant="body2" sx={{ color: "#64748b" }}>{todayLabel}</Typography>
                            </Box>
                        </Stack>
                        <Stack direction="row" gap={1}>
                            {isLoading ? <CircularProgress size={24} /> : null}
                            <Button variant="outlined" startIcon={<LogoutRounded />} onClick={() => setIsLogoutDialogOpen(true)}>
                                Wyloguj
                            </Button>
                        </Stack>
                    </Stack>
                </Paper>

                {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", lg: "minmax(300px, 34%) minmax(0, 66%)" },
                        gap: 2,
                        alignItems: "start",
                    }}
                >
                    <Paper elevation={0} sx={{ borderRadius: 2, border: "1px solid #dbe3ef", bgcolor: "#fff", overflow: "hidden" }}>
                        <Box sx={{ p: 2 }}>
                            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} gap={1}>
                                <Box>
                                    <Typography sx={{ fontWeight: 950, color: "#0f172a" }}>
                                        {isTodaySelected ? "Dzisiaj" : "Wybrany dzień"}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: "#64748b" }}>
                                        {selectedDateLabel}
                                    </Typography>
                                </Box>
                                {!isTodaySelected ? (
                                    <Button
                                        variant="contained"
                                        startIcon={<TodayRounded />}
                                        onClick={returnToToday}
                                        sx={{ fontWeight: 900, alignSelf: { xs: "stretch", sm: "center" } }}
                                    >
                                        Powrót do dnia bieżącego
                                    </Button>
                                ) : null}
                            </Stack>
                            <Typography variant="body2" sx={{ color: "#64748b" }}>
                                {selectedDayLogs.length} awizacji
                            </Typography>
                        </Box>
                        <Divider />
                        <Stack spacing={1} sx={{ p: 1.5 }}>
                            {selectedDayLogs.length === 0 ? (
                                <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: "1px dashed #cbd5e1", bgcolor: "#f8fafc", textAlign: "center" }}>
                                    <Typography sx={{ fontWeight: 850 }}>
                                        {isTodaySelected ? "Brak awizacji na dzisiaj" : "Brak awizacji na wybrany dzień"}
                                    </Typography>
                                </Paper>
                            ) : null}
                            {selectedDayLogs.map((log) => (
                                <Paper key={`${log.factory._id}-${log.notice._id}`} elevation={0} sx={{ p: 1.25, borderRadius: 1.5, border: log.notice.isCompleted ? "1px solid #86efac" : "1px solid #e5e7eb", opacity: selectedDateKey < todayKey && !log.notice.isCompleted ? 0.65 : 1, bgcolor: log.notice.isCompleted ? "#f0fdf4" : selectedDateKey < todayKey ? "#f8fafc" : "#fff" }}>
                                    <Stack direction="row" gap={1.25} alignItems="flex-start">
                                        <FactoryAvatar factory={log.factory} size={48} />
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography sx={{ fontWeight: 950, color: "#0f172a", wordBreak: "break-word", textDecoration: log.notice.isCompleted ? "line-through" : "none" }}>
                                                {log.factory.factoryName}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: "#64748b" }}>
                                                {getNoticeRange(log.notice)}
                                            </Typography>
                                            {log.notice.message ? (
                                                <Typography variant="body2" sx={{ color: "#334155", mt: 0.75 }}>
                                                    {log.notice.message}
                                                </Typography>
                                            ) : null}
                                            {log.notice.isCompleted ? <Chip size="small" color="success" icon={<CheckCircleRounded />} label="Dostawa zrealizowana" sx={{ mt: 1, fontWeight: 850 }} /> : null}
                                        </Box>
                                        {session.hasPermission('deliveryNotices.complete') ? (
                                            <Button
                                                size="small"
                                                variant={log.notice.isCompleted ? "outlined" : "contained"}
                                                color={log.notice.isCompleted ? "warning" : "success"}
                                                startIcon={log.notice.isCompleted ? <UndoRounded /> : <CheckCircleRounded />}
                                                disabled={updatingNoticeId === log.notice._id}
                                                onClick={() => setNoticeCompleted(log.factory._id, log.notice, !log.notice.isCompleted)}
                                                sx={{ flex: "0 0 auto", fontWeight: 900, whiteSpace: "nowrap" }}
                                            >
                                                {updatingNoticeId === log.notice._id ? "Zapisywanie…" : log.notice.isCompleted ? "Cofnij oznaczenie" : "Oznacz jako dostarczoną"}
                                            </Button>
                                        ) : null}
                                    </Stack>
                                </Paper>
                            ))}
                        </Stack>
                    </Paper>

                    <Paper elevation={0} sx={{ borderRadius: 2, border: "1px solid #dbe3ef", bgcolor: "#fff", overflow: "hidden" }}>
                        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} gap={1} sx={{ p: 2 }}>
                            <Stack direction="row" alignItems="center" gap={1}>
                                <Button variant="outlined" onClick={() => changeMonth(-1)} sx={{ minWidth: 40, px: 1 }}>
                                    <ChevronLeftRounded />
                                </Button>
                                <Typography sx={{ minWidth: 190, textAlign: "center", fontWeight: 950, textTransform: "capitalize" }}>
                                    {monthLabel(month)}
                                </Typography>
                                <Button variant="outlined" onClick={() => changeMonth(1)} sx={{ minWidth: 40, px: 1 }}>
                                    <ChevronRightRounded />
                                </Button>
                            </Stack>
                            <Chip color="primary" variant="outlined" label="Widok pn-sb" />
                        </Stack>
                        <Divider />
                        <Box sx={{ p: { xs: 1, md: 2 } }}>
                            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: 1, mb: 1 }}>
                                {["Pon", "Wt", "Sr", "Czw", "Pt", "Sob"].map((label) => (
                                    <Typography key={label} variant="caption" sx={{ textAlign: "center", fontWeight: 900, color: "#64748b" }}>
                                        {label}
                                    </Typography>
                                ))}
                            </Box>
                            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: 1 }}>
                                {monthDays.map((date, index) => {
                                    if (!date) return <Box key={`blank-${index}`} sx={{ minHeight: { xs: 74, md: 108 } }} />;

                                    const key = toDateKey(date);
                                    const logs = calendarLogs.get(key) || [];
                                    const isToday = key === todayKey;
                                    const isSelected = key === selectedDateKey;
                                    const isPast = key < todayKey;

                                    return (
                                        <Paper
                                            key={key}
                                            elevation={0}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => selectDate(date)}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter" || event.key === " ") {
                                                    event.preventDefault();
                                                    selectDate(date);
                                                }
                                            }}
                                            sx={{
                                                minHeight: { xs: 74, md: 108 },
                                                p: 1,
                                                borderRadius: 1.25,
                                                border: isSelected ? "2px solid #f97316" : isToday ? "2px solid #1d4ed8" : logs.length ? (isPast ? "1px solid #cbd5e1" : "1px solid #93c5fd") : "1px solid #e5e7eb",
                                                bgcolor: isSelected ? "#fff7ed" : isToday ? "#dbeafe" : logs.length ? (isPast ? "#f8fafc" : "#eff6ff") : "#fff",
                                                cursor: "pointer",
                                                overflow: "visible",
                                                boxShadow: isToday ? "inset 0 0 0 1px rgba(29, 78, 216, 0.18)" : "none",
                                                transition: "border-color 120ms ease, background-color 120ms ease, transform 120ms ease",
                                                "&:hover": {
                                                    borderColor: isSelected ? "#f97316" : "#2563eb",
                                                    transform: "translateY(-1px)",
                                                },
                                                "&:focus-visible": {
                                                    outline: "3px solid rgba(37, 99, 235, 0.35)",
                                                    outlineOffset: 2,
                                                },
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={0.5} sx={{ mb: 0.75 }}>
                                                <Typography sx={{ fontWeight: 950, color: isPast ? "#94a3b8" : "#0f172a" }}>
                                                    {date.getDate()}
                                                </Typography>
                                                {isToday ? (
                                                    <Chip size="small" color="primary" label="Dziś" sx={{ height: 20, fontWeight: 900 }} />
                                                ) : null}
                                            </Stack>
                                            <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                                                {logs.map((log) => (
                                                    <Stack
                                                        key={`${key}-${log.factory._id}-${log.notice._id}`}
                                                        direction="row"
                                                        alignItems="center"
                                                        gap={0.5}
                                                        minWidth={0}
                                                        role={session.hasPermission('deliveryNotices.complete') ? "button" : undefined}
                                                        tabIndex={session.hasPermission('deliveryNotices.complete') ? 0 : undefined}
                                                        title={session.hasPermission('deliveryNotices.complete') ? (log.notice.isCompleted ? "Cofnij oznaczenie dostawy" : "Oznacz dostawę jako zrealizowaną") : undefined}
                                                        aria-label={session.hasPermission('deliveryNotices.complete') ? `${log.notice.isCompleted ? "Cofnij oznaczenie" : "Oznacz jako dostarczoną"}: ${log.factory.factoryName}` : undefined}
                                                        onClick={(event) => {
                                                            if (!session.hasPermission('deliveryNotices.complete')) return;
                                                            event.stopPropagation();
                                                            setNoticeCompleted(log.factory._id, log.notice, !log.notice.isCompleted);
                                                        }}
                                                        onKeyDown={(event) => {
                                                            if (!session.hasPermission('deliveryNotices.complete') || (event.key !== "Enter" && event.key !== " ")) return;
                                                            event.preventDefault();
                                                            event.stopPropagation();
                                                            setNoticeCompleted(log.factory._id, log.notice, !log.notice.isCompleted);
                                                        }}
                                                        sx={{
                                                            opacity: isPast || log.notice.isCompleted ? 0.68 : 1,
                                                            cursor: session.hasPermission('deliveryNotices.complete') ? "pointer" : "default",
                                                            borderRadius: 1,
                                                            px: 0.35,
                                                            py: 0.2,
                                                            bgcolor: log.notice.isCompleted ? "rgba(22, 163, 74, 0.10)" : "transparent",
                                                            "&:hover": { bgcolor: session.hasPermission('deliveryNotices.complete') ? (log.notice.isCompleted ? "rgba(217, 119, 6, 0.12)" : "rgba(22, 163, 74, 0.12)") : undefined },
                                                            "&:focus-visible": { outline: "2px solid #2563eb", outlineOffset: 1 },
                                                        }}
                                                    >
                                                        <FactoryAvatar factory={log.factory} size={22} />
                                                        <Typography variant="caption" sx={{ fontWeight: 800, color: isPast || log.notice.isCompleted ? "#64748b" : "#1e3a8a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: log.notice.isCompleted ? "line-through" : "none" }}>
                                                            {log.factory.factoryName}
                                                        </Typography>
                                                        {session.hasPermission('deliveryNotices.complete') ? (log.notice.isCompleted ? <CheckCircleRounded sx={{ ml: "auto", fontSize: 15, color: "#15803d" }} /> : <RadioButtonUncheckedRounded sx={{ ml: "auto", fontSize: 15, color: "#64748b" }} />) : null}
                                                    </Stack>
                                                ))}
                                            </Stack>
                                        </Paper>
                                    );
                                })}
                            </Box>
                        </Box>
                    </Paper>
                </Box>
            </Stack>

            <Dialog open={isLogoutDialogOpen} onClose={() => setIsLogoutDialogOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 950 }}>Wylogować?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: "#475569" }}>
                        Po wylogowaniu ponowne wejście do widoku awizacji będzie wymagało podania hasła.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button variant="outlined" onClick={() => setIsLogoutDialogOpen(false)}>
                        Anuluj
                    </Button>
                    <Button variant="contained" color="error" startIcon={<LogoutRounded />} onClick={handleLogout}>
                        Wyloguj
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FactoryDeliveryNoticeBoard;
